import { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import {
  Radar,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  User,
  Package,
  AlertTriangle,
  Ban,
  RefreshCw,
  Activity,
  ShieldAlert,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../lib/toast';

const DETECTION_TYPES = {
  dropship_sweep: { label: 'Dropship/Reseller Sweep', color: 'bg-amber-100 text-amber-700', icon: ShieldAlert },
  duplicate_listing: { label: 'Duplicate Listing', color: 'bg-purple-100 text-purple-700', icon: Package },
  prohibited_item: { label: 'Prohibited Item', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

const STATUS_BADGES = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  reviewed: { label: 'Reviewed', color: 'bg-blue-100 text-blue-700', icon: Eye },
  actioned: { label: 'Actioned', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  dismissed: { label: 'Dismissed', color: 'bg-gray-100 text-gray-600', icon: XCircle },
};

const formatDate = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

function parseDetails(details) {
  if (!details) return null;
  if (typeof details === 'object') return details;
  try {
    return JSON.parse(details);
  } catch {
    return null;
  }
}

function ScoreBar({ score, max = 100 }) {
  const pct = Math.min(100, Math.max(0, (score / max) * 100));
  let barColor = 'bg-yellow-400';
  if (pct >= 80) barColor = 'bg-red-500';
  else if (pct >= 60) barColor = 'bg-orange-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-bold text-gray-700 w-10 text-right">{score}</span>
    </div>
  );
}

function SignalList({ signals }) {
  if (!signals || !Array.isArray(signals) || signals.length === 0) return null;
  return (
    <div className="space-y-1">
      {signals.map((signal, i) => (
        <div key={i} className="flex items-start gap-2 text-sm">
          <Activity className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
          <span className="text-gray-600">{signal}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminDetectionsPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [expandedReport, setExpandedReport] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [bulkDismissing, setBulkDismissing] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [statusFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ reporter_type: 'system', limit: '50' });
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/reports/admin/all?${params}`);
      setReports(res.data || []);
    } catch (err) {
      console.error('Failed to load detections:', err);
      toast.error('Failed to load detections');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/reports/admin/stats');
      setStats(res.data);
    } catch (err) {
      console.log('Stats not available');
    }
  };

  const handleAction = async (reportId, action) => {
    setActionLoading(reportId);
    try {
      await api.post(`/reports/admin/${reportId}/action?action=${action}`, {
        admin_notes: adminNotes || null
      });
      const actionLabels = { dismiss: 'dismissed', warn: 'warned user', hide: 'hidden listing', ban: 'banned user' };
      toast.success(`Detection ${actionLabels[action] || 'updated'}`);
      setAdminNotes('');
      setExpandedReport(null);
      fetchReports();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to action detection');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkDismiss = async () => {
    const pendingReports = reports.filter(r => r.status === 'pending');
    if (pendingReports.length === 0) return;

    if (!window.confirm(`Dismiss all ${pendingReports.length} pending detections as false positives?`)) return;

    setBulkDismissing(true);
    let dismissed = 0;
    for (const report of pendingReports) {
      try {
        await api.post(`/reports/admin/${report.id}/action?action=dismiss`, {
          admin_notes: 'Bulk dismissed as false positive'
        });
        dismissed++;
      } catch (err) {
        console.error(`Failed to dismiss ${report.id}:`, err);
      }
    }
    toast.success(`Dismissed ${dismissed} of ${pendingReports.length} detections`);
    setBulkDismissing(false);
    fetchReports();
    fetchStats();
  };

  const getStatusBadge = (status) => {
    const config = STATUS_BADGES[status] || STATUS_BADGES.pending;
    const Icon = config.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getDetectionBadge = (reason) => {
    const config = DETECTION_TYPES[reason] || { label: reason, color: 'bg-gray-100 text-gray-600' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const pendingCount = reports.filter(r => r.status === 'pending').length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Radar className="w-6 h-6 text-amber-500" />
              Automatic Detections
            </h1>
            <p className="text-gray-500">System-generated reports from automated sweeps and detection rules</p>
          </div>
          <div className="flex items-center gap-2">
            {statusFilter === 'pending' && pendingCount > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkDismiss}
                disabled={bulkDismissing}
                className="flex items-center gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                {bulkDismissing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Dismiss All ({pendingCount})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => { fetchReports(); fetchStats(); }} className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-yellow-600 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Pending</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.pending || 0}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <Eye className="w-4 h-4" />
                <span className="text-sm font-medium">Reviewed</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.reviewed || 0}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Actioned</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.actioned || 0}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <XCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Dismissed</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.dismissed || 0}</p>
            </div>
          </div>
        )}

        {/* Status Filter */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['pending', 'reviewed', 'actioned', 'dismissed', ''].map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s)}
              className={statusFilter === s ? 'bg-[#1E3A5F]' : ''}
            >
              {s ? (STATUS_BADGES[s]?.label || s) : 'All'}
              {stats && s && <span className="ml-1 text-xs opacity-70">({stats[s] || 0})</span>}
            </Button>
          ))}
        </div>

        {/* Detections List */}
        <div className="space-y-4">
          {reports.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
              <Radar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No detections</h3>
              <p className="text-gray-500">
                {statusFilter ? `No ${statusFilter} automatic detections found` : 'No automatic detections have been generated'}
              </p>
            </div>
          ) : (
            reports.map((report) => {
              const details = parseDetails(report.details);
              const score = details?.score;
              const signals = details?.signals;

              return (
                <div key={report.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  {/* Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <ShieldAlert className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-gray-900">
                              {report.item_title || 'Unknown User'}
                            </p>
                            {getDetectionBadge(report.reason)}
                            {score != null && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                score >= 80 ? 'bg-red-100 text-red-700' : score >= 60 ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                Score: {score}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            Auto-detected {' \u2022 '}
                            {formatDate(report.created_at)}
                            {details?.active_listings != null && (
                              <> {' \u2022 '} {details.active_listings} active listings</>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(report.status)}
                        {expandedReport === report.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedReport === report.id && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Flagged User */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Flagged User</h4>
                          <div className="bg-white p-3 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <p className="font-medium text-gray-900">{report.item_title || 'Unknown'}</p>
                            </div>
                            <p className="text-xs text-gray-400 font-mono mt-1">User ID: {report.item_owner_id || report.item_id}</p>
                            <a
                              href={`/admin/users?highlight=${report.item_owner_id || report.item_id}`}
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2"
                            >
                              <ExternalLink className="w-3 h-3" /> View in User Management
                            </a>
                          </div>
                        </div>

                        {/* Score Breakdown */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Detection Score</h4>
                          <div className="bg-white p-3 rounded-lg border border-gray-200">
                            {score != null ? (
                              <>
                                <ScoreBar score={score} />
                                <p className="text-xs text-gray-500 mt-2">
                                  Threshold: 60 {' \u2022 '} {score >= 80 ? 'High confidence' : score >= 60 ? 'Above threshold' : 'Below threshold'}
                                </p>
                              </>
                            ) : (
                              <p className="text-sm text-gray-400 italic">No score data available</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Signals */}
                      {signals && signals.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Detection Signals</h4>
                          <div className="bg-white p-3 rounded-lg border border-gray-200">
                            <SignalList signals={signals} />
                          </div>
                        </div>
                      )}

                      {/* Raw details text if not JSON */}
                      {!details && report.details && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Details</h4>
                          <div className="bg-white p-3 rounded-lg border border-gray-200">
                            <p className="text-gray-600 text-sm whitespace-pre-wrap">{report.details}</p>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      {(report.status === 'pending' || report.status === 'reviewed') && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Admin Notes</h4>
                          <Textarea
                            placeholder="Add notes about your decision (optional)..."
                            value={expandedReport === report.id ? adminNotes : ''}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            className="mb-3 bg-white"
                            rows={2}
                          />
                          <div className="flex gap-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => handleAction(report.id, 'dismiss')} disabled={actionLoading === report.id} className="border-gray-300">
                              {actionLoading === report.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1" /> Dismiss (False Positive)</>}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleAction(report.id, 'warn')} disabled={actionLoading === report.id} className="border-yellow-300 text-yellow-700 hover:bg-yellow-50">
                              <AlertTriangle className="w-4 h-4 mr-1" /> Warn User
                            </Button>
                            <Button size="sm" onClick={() => handleAction(report.id, 'ban')} disabled={actionLoading === report.id} className="bg-red-600 hover:bg-red-700 text-white">
                              <Ban className="w-4 h-4 mr-1" /> Escalate to Ban
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Resolution info */}
                      {(report.status === 'actioned' || report.status === 'dismissed') && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="bg-white p-3 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Action taken:</span> {report.action_taken || 'N/A'}
                            </p>
                            {report.admin_notes && (
                              <p className="text-sm text-gray-500 mt-1"><span className="font-medium">Notes:</span> {report.admin_notes}</p>
                            )}
                            {report.actioned_at && (
                              <p className="text-xs text-gray-400 mt-2">Processed: {formatDate(report.actioned_at)}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
